import React, { useState } from 'react';
import { Check, Copy, KeyRound, X, Clock } from 'lucide-react';

/**
 * Shows the activation code that "Generate new code" just minted for an
 * EXISTING till (same register, same location). Super-admin modal standard:
 * black header + ruby underline + ruby icon tile, white body, black button.
 */
export interface NewTerminalCode {
  terminalId: string;
  registerName?: string;
  code: string;
  expiresAt: string;
  reopened?: boolean;
  message?: string;
}

/**
 * The terminals list masks a code once it can no longer be used (activated,
 * released or expired → RT-****-AB12). Returns the code only when it is the
 * real, enterable one — never tell anyone to "enter RT-****-AB12".
 */
export const usableTerminalCode = (t: { provisionCode?: string | null; provisionCodeMasked?: boolean; codeExpired?: boolean } | null | undefined): string | null => {
  const c = t?.provisionCode;
  if (!c || t?.provisionCodeMasked || t?.codeExpired || String(c).includes('*')) return null;
  return String(c);
};

export const formatExpiry = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const TerminalNewCodeModal: React.FC<{ result: NewTerminalCode | null; onClose: () => void }> = ({ result, onClose }) => {
  const [copied, setCopied] = useState(false);
  if (!result) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — the code is selectable */ }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-black px-5 py-4 flex items-center gap-3 border-b-2 border-[#9F1239]">
          <div className="w-11 h-11 rounded-xl bg-[#9F1239] flex items-center justify-center shrink-0">
            <KeyRound size={20} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-base">New activation code</h3>
            {result.registerName && <p className="text-stone-400 text-xs truncate">{result.registerName}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-stone-400 hover:text-white rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 text-center">
          <p className="text-stone-600 text-sm mb-4">Enter this code on the till. It works once.</p>
          <div className="inline-flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-xl px-5 py-3">
            <span className="text-2xl font-mono font-bold text-stone-900 tracking-widest select-all">{result.code}</span>
            <button
              type="button"
              onClick={copy}
              aria-label="Copy code"
              className="rounded-lg bg-white border border-stone-200 hover:bg-stone-100 text-stone-600 min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              {copied ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
            </button>
          </div>
          <p className="text-stone-500 text-xs mt-3 flex items-center justify-center gap-1.5">
            <Clock size={12} /> Expires {formatExpiry(result.expiresAt)}
          </p>
          {result.reopened && (
            <p className="text-stone-700 text-xs mt-3">This register was released. The new code re-opens the same register with all its settings.</p>
          )}
          <p className="text-stone-500 text-xs mt-3">The old code no longer works and the old device has been signed out.</p>
          <button
            type="button"
            onClick={onClose}
            className="mt-5 w-full px-4 py-3 bg-black text-white font-semibold rounded-xl hover:bg-stone-800 min-h-[44px]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default TerminalNewCodeModal;
